/**
 * Web Push on the browser side.
 *
 * The pieces that differ between browsers live here:
 *
 *   - Chrome and Firefox both require the permission request to happen
 *     inside a user gesture, so `requestPermission()` is only ever called
 *     from the settings toggle's click handler — never on mount.
 *   - Firefox for Android has no Push API, so `subscribe()` fails and the
 *     toggle has to say so rather than pretending it worked.
 *   - The subscription's `applicationServerKey` is base64url and has to be
 *     handed to `subscribe()` as bytes.
 */

import { api } from '@/lib/api'

/** Where the service worker lives. It must be at the root to cover all URLs. */
const SW_PATH = '/sw.js'

export type PermissionState = 'granted' | 'denied' | 'default' | 'unsupported'

export function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

/** The browser's current permission state. */
export function permissionState(): PermissionState {
  if (!isPushSupported()) return 'unsupported'
  return Notification.permission as PermissionState
}

/** Register the service worker, returning its registration. */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration(SW_PATH)
  if (existing) return existing
  return navigator.serviceWorker.register(SW_PATH, { scope: '/' })
}

/**
 * Base64url → bytes, for `applicationServerKey`.
 *
 * The buffer type is named so it satisfies `BufferSource`, which wants a
 * view over a concrete `ArrayBuffer`.
 */
function base64UrlToBytes(value: string): Uint8Array<ArrayBuffer> {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/')
  const binary = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4))
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes
}

/**
 * Ask for permission, subscribe and register the subscription with the
 * server. Must be called from a user gesture.
 *
 * Returns `false` when the user declined or the browser cannot push.
 */
export async function enablePush(vapidPublicKey: string): Promise<boolean> {
  if (!isPushSupported()) return false

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return false

  const registration = await registerServiceWorker()
  // The subscription needs a controller, so wait for the SW to be ready.
  await navigator.serviceWorker.ready

  const subscription =
    (await registration.pushManager.getSubscription()) ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: base64UrlToBytes(vapidPublicKey),
    }))

  const json = subscription.toJSON()
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return false

  await api('/api/v1/me/push/subscriptions', {
    method: 'POST',
    body: { endpoint: json.endpoint, keys: { p256dh: json.keys.p256dh, auth: json.keys.auth } },
  })

  return true
}

/** Remove this browser's subscription. */
export async function disablePush(): Promise<void> {
  if (!isPushSupported()) return
  const registration = await navigator.serviceWorker.getRegistration(SW_PATH)
  const subscription = await registration?.pushManager.getSubscription()
  if (subscription) {
    await api('/api/v1/me/push/subscriptions', {
      method: 'DELETE',
      query: { endpoint: subscription.endpoint },
    })
    await subscription.unsubscribe()
  }
  // Turn the channel off for this account too.
  await api('/api/v1/me/notification-prefs', {
    method: 'PATCH',
    body: { pushEnabled: false },
  })
}

/** Whether this browser currently holds a push subscription. */
export async function hasSubscription(): Promise<boolean> {
  if (!isPushSupported()) return false
  const registration = await navigator.serviceWorker.getRegistration(SW_PATH)
  const subscription = await registration?.pushManager.getSubscription()
  return Boolean(subscription)
}
